/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';
import groupBy from 'lodash/groupBy';

import IcalWeekday from './ical-weekday';

// TODO Vkládat do PRODID text-description verzi aplikace.
// TODO Vkládat do PRODID konkrétní třídu, která provdela jeho generování.
// TODO Možnost nastavení hodnot (názvy v konfiguraci, verze z package)
/**
 * Identifikátor aplikace, která kalendář vygenerovala.
 * Používá se formát dle RFC 3151, jak to doporučuje RFC 5545:
 * `prefix//owner-identifier//text-class text-description//language`
 * - prefix + znamená, že jde o registrovaný identifikátor (díky IDN),
 *   který by měl být jedinečný na pro všechny vydavatele SW.
 * - owner-identifier je v IDN formátu, který vydavatele spojuje
 *   s majitelem uvedené domény
 * - text-class NONSGML označuje, že dokument není v jazyce SGML.
 * - text-description s názvem aplikace
 * - language s jazykem aplikace
 */
export const defaultProdid = '+//IDN ki-wi.cz//NONSGML Player Lighter//EN';
/**
 * Verze iCalendar specifikace, ve které je kalendář uložen.
 * Hodnota 2.0 se používá pro RFC 2445 i 5545.
 * Verze 1.0 tak pravděpodobně odpovídá historickému vCalendar formátu,
 * na jehož základě vznikl iCalendar.
 * Asi nedává smysl nastavovat na něco jiného, jelikož to iCal API
 * nepodporuje.
 */
export const defaultVersion = '2.0';

/**
 * Utility pro iCalendar API
 *
 * Zdroje:
 * - [iCal.js doc](https://mozilla-comm.github.io/ical.js/api/index.html)
 * - [iCal.js wiki](https://github.com/mozilla-comm/ical.js/wiki)
 */
class IcalUtil {
  /**
   * @param {IcalApi} ICAL
   */
  constructor(ICAL) {
    this.ICAL = ICAL;
  }

  /**
   * Vytvoří kopii komponenty.
   * Tato metoda v API chybí.
   * @param {IcalApi.Component} component
   * @returns {IcalApi.Component}
   */
  cloneComponent(component) {
    return new this.ICAL.Component(component.toJSON());
  }

  /**
   * Vytvoří kopii vlastnosti.
   * Tato metoda v API chybí.
   * @param {IcalApi.Property} property
   * @returns {IcalApi.Property}
   */
  cloneProperty(property) {
    return new this.ICAL.Property(property.toJSON());
  }

  // TODO Jak lépe přidat EXDATE?
  // - Při použití addPropertyWithValue nelze přidat více hodnot.
  // - https://github.com/mozilla-comm/ical.js/issues/436
  /**
   * Vytvoří EXDATE s více datumy oddělenými čárkou.
   * @param {Array.<IcalApi.Time>} times
   * @returns {IcalApi.Property}
   */
  createExdateProperty(times) {
    const strings = times.map((time) => time.toICALString());
    return new this.ICAL.Property(['exdate', {}, 'date-time', ...strings]);
  }

  // TODO Nemělo by být pojmenováni spíše `createVcalendar`?
  // - Anebo tedy přejenovat ve třídách `vcalendar` na `vCalendar`
  /**
   * Vytvoří VCALENDAR s pohodlnějším API.
   *
   * Důvody:
   * - Jednotné pořadí položek na výstupu, bez ohledu pořadí vstupů.
   * - Immutable přístup k vytváření událostí,
   *   než postupné přidávání hodnot do již vytvořeného objektu.
   * @param {Object} opts
   * @param {string} opts.prodid Identifikátor aplikace, která kalendář
   * vygenerovala.
   * @param {Array.<IcalApi.Component>} [opts.subcomponents]
   * @param {string} opts.version Verze iCalendar specifikace, ve které
   * je kalendář uložen.
   * Pro běžné použití není potřeba nastavovat na jinou než výchozí hodnotu.
   * @returns {IcalApi.Component}
   */
  createVCalendar({
    prodid = defaultProdid,
    subcomponents = [],
    version = defaultVersion
  }) {
    const vcalendar = new this.ICAL.Component(['vcalendar', [], []]);
    vcalendar.updatePropertyWithValue('version', version);
    vcalendar.updatePropertyWithValue('prodid', prodid);
    subcomponents.forEach((subcomponent) => {
      vcalendar.addSubcomponent(subcomponent);
    });
    return vcalendar;
  }

  // TODO Povinné položky:
  // - startDate je povinné vždy
  // - uid může být nepovinné, pokud by se následně vygenerovalo,
  //   ale o to není zájem jak v aplikaci, tak v testech
  // - endDate může být nepovinné, vytvoří se tak událost s délkou 1 den
  // - stamp může být nepovinné, použije se aktuální čas, ale o to není zájem
  //   jak v aplikaci, tak v testech
  // TODO Nemělo by být pojmenováni spíše createVevent?
  // - Anebo tedy přejenovat ve třídách `vevent` na `vEvent`?
  // TODO Vracet spíše IcalApi.Event, kde je pak položka component?
  // TODO Je IcalApi.Event#component veřejným API?
  // - http://mozilla-comm.github.io/ical.js/api/event.js.html#line38
  /**
   * Vytvoří VEVENT s pohodlnějším API.
   *
   * Důvody:
   * - iCal API nemá vhodně ošetřenu reakci na vstupy jako `""` a `[]`.
   *   Vytváří prázdné komponenty, nebo vyhazuje chyby.
   *   Tato metoda takové vstupy považuje za nevyplněné položky.
   * - Jednotné pořadí položek na výstupu, bez ohledu pořadí vstupů.
   * - Immutable přístup k vytváření událostí,
   *   než postupné přidávání hodnot do již vytvořeného objektu.
   * @param {Object} opts
   * @param {IcalApi.Time} opts.endDate
   * @param {Array.<IcalApi.Time>} [opts.excludedDates]
   * @param {IcalApi.Recur} [opts.recur]
   * @param {IcalApi.Time} [opts.recurrenceDate]
   * @param {IcalApi.Time} opts.stamp
   * @param {IcalApi.Time} opts.startDate
   * @param {string} [opts.summary]
   * @param {string} opts.uid
   * @returns {IcalApi.Component}
   */
  createVEvent({
    endDate,
    excludedDates = [],
    recur,
    recurrenceDate,
    stamp,
    startDate,
    summary = '',
    uid
  }) {
    const { ICAL } = this;
    const vevent = new ICAL.Component('vevent');
    const ievent = new ICAL.Event(vevent);
    ievent.uid = uid;
    vevent.addPropertyWithValue('dtstamp', stamp);
    ievent.startDate = startDate;
    ievent.endDate = endDate;
    if (!isNil(recur)) {
      vevent.addPropertyWithValue('rrule', recur);
    }
    if (excludedDates.length) {
      const property = this.createExdateProperty(excludedDates);
      vevent.addProperty(property);
    }
    if (!isNil(recurrenceDate)) {
      ievent.recurrenceId = recurrenceDate;
    }
    if (summary.length) {
      ievent.summary = summary;
    }
    return vevent;
  }

  /**
   * Zjistí všechny události z kalendáře včetně jejich vazeb.
   * Metoda byla přidána, protože vzhledem k bugům API není úplně triviální
   * tohoto dosáhnout při udržení rozumného výkonu.
   * @param {IcalApi.Component} calendar
   * @returns {Array.<IcalApi.Event>} Seznam rodičovských událostí.
   * Je zaručeno, že žádná z těchto událostí nesplňuje
   * {@link IcalApi.Event.isRecurrenceException}.
   * K událostem výjimek lze přistupovat z rodičovské události přes
   * {@link IcalApi.Event.exceptions}.
   */
  getAllEvents(calendar) {
    const { ICAL } = this;
    // Nejprve se vyhledají a vytvoří všechny události bez vazeb.
    // To je důležité, jinak by vyhledání vazeb začalo provádět iCal API
    // a to s bugy a špatnou výkonností.
    // Následně se mezi vytvořenými událostmi vytvoří vazby.
    return this.relateEvents(
      calendar
        .getAllSubcomponents('vevent')
        .map((component) => new ICAL.Event(component, { exceptions: [] }))
    );
  }

  /**
   * Zjistí hodnoty BY* části vlastnosti RRULE.
   * Oproti originálnímu zpracování vrací vždy pole, aby uživatel metody nemusel
   * tento případ speciálně ošetřovat.
   * @param {string} by Hodnoty BYSECOND, BYMINUTE atd.
   * @param {Object} parts
   * @returns {Array.<number|string>}
   */
  getByPartValues(by, parts) {
    // TODO Číselník pro by
    const values = parts[by];
    if (isNil(values)) {
      return [];
    }
    return values;
  }

  /**
   * Zjistí nepovinný čas konce intervalu.
   * @param {Object} [opts={}]
   * @param {IcalApi.Time} [opts.endDate]
   * @private
   * @returns {number}
   */
  getEndTime({ endDate } = {}) {
    if (isNil(endDate)) {
      return Infinity;
    }
    return endDate.toUnixTime();
  }

  /**
   * Zjistí nepovinný čas začátku intervalu.
   * @param {Object} [opts={}]
   * @param {IcalApi.Time} [opts.startDate]
   * @private
   * @returns {number}
   */
  getStartTime({ startDate } = {}) {
    if (isNil(startDate)) {
      return -Infinity;
    }
    return startDate.toUnixTime();
  }

  /**
   * Zjistí, jestli je neopakovaná událost v zadaném časovém intervalu.
   * Stejně jako události je hledaný interval zleva uzavřený a zleva otevřený.
   * Libovolné ohraničující datum lze vynechat a pak z dané strany nebudou
   * události omezené.
   * Lze použít i pro vyhledání události v jeden konkrétní okamžik,
   * v tom případě se musí `startDate` a `endDate` nastavit na stejné datum.
   * @param {IcalApi.Event|IcalApi.Event.occurrenceDetails} event
   * @param {Object} [opts={}]
   * @param {IcalApi.Time} [opts.endDate]
   * @param {IcalApi.Time} [opts.startDate]
   * @returns {boolean}
   */
  isEventWithinRange(event, { endDate, startDate } = {}) {
    // Určí se rozsah časů, ve kterém se bude hledat.
    const endTime = this.getEndTime({ endDate });
    const startTime = this.getStartTime({ startDate });
    // Začátek intervalu musí být před koncem, jinak požadavek nedává smysl.
    if (startTime > endTime) {
      return false;
    }
    const eventEndTime = event.endDate.toUnixTime();
    const eventStartTime = event.startDate.toUnixTime();
    // Kontrola validity události dle RFC 5545 kapitola 3.8.2.2.:
    // The value type of this property MUST be the same as the "DTSTART"
    // property, and its value MUST be later in time than the value
    // of the "DTSTART" property.
    // Události bez kladné DURATION se přeskakují.
    // TODO Implementace pro události s nulovou duration.
    // - Podle specifikace asi není validní, ale teoreticky nimi lze počítat.
    // - Zde zrušit ostrou rovnost, čímž se tyto události povolí.
    //   Potom je nutné odkomentovat dva bloky v kódu níže.
    if (eventStartTime >= eventEndTime) {
      return false;
    }
    // Pokud se nehledá v rozsahu, ale pouze jednom okamžiku, tak tento okamžik
    // musí být v intervalu <eventStartDate, eventEndDate).
    // To vychází z RFC 5545 kapitola 3.6.1.:
    // The "DTSTART" property for a "VEVENT" specifies the inclusive start
    // of the event.
    // ...
    // The "DTEND" property for a "VEVENT" calendar component specifies
    // the non-inclusive end of the event.
    if (startTime === endTime) {
      // TODO Implementace pro události s nulovou duration.
      // if (startTime === endTime) {
      //   return startTime === eventStartTime;
      // }
      return startTime >= eventStartTime && startTime < eventEndTime;
    }
    // TODO Implementace pro události s nulovou duration.
    // if (startTime === endTime) {
    //   return (startTime >= eventStartTime) && (startTime < eventEndTime);
    // }
    // Pokud se hledá v rozsahu, tak musí mít událost alespoň jeden společný
    // okamžik s rozsahem.
    // Stejně jako událost (viz. výše) je rozsah v intervalu zleva uzavřeném
    // a zprava otevřeném.
    // Proto zde není nikde rovnost, protože ať událost navazuje na interval
    // ať zleva, nebo zprava, tak nemají společný okamžik, protože se překrývá
    // otevření a uzavřený okamžik.
    return endTime > eventStartTime && startTime < eventEndTime;
  }

  /**
   * Zjistí, jestli je opakovaná událost v zadaném časovém intervalu.
   * Stejně jako události je hledaný interval zleva uzavřený a zleva otevřený.
   * Libovolné ohraničující datum lze vynechat a pak z dané strany nebudou
   * události omezené.
   * Lze použít i pro vyhledání události v jeden konkrétní okamžik,
   * v tom případě se musí `startDate` a `endDate` nastavit na stejné datum.
   *
   * Nefunguje dokonale, pouze vyřadí události u kterých je jisté, že se jejich
   * výskyty v daném intervalu neobjeví.
   * Nezaručuje, že událost označená jako vyhovující má skutečně v daný interval
   * aktivní výskyt.
   * - Neuvažuje výjimky z opakování.
   *   Uživatel je zodpovědný za to, že na vstup nevloží opakovanou událost
   *   se změněnými instancemi opakování.
   *   V tom případě může metoda vracet `false` i když bude změněná instance
   *   ve skutečnosti aktivní.
   * - Nepodporuje RDATE a více RRULE (složitý algoritmus).
   * - Neuvažuje omezení opakování pomocí COUNT (složitý algoritmus).
   * - Neuvažuje "díry" v opakované události, považuje ji za nepřerušovanou.
   * @param {IcalApi.Event} event
   * @param {IcalApi.Recur} recur
   * @param {Object} [opts={}]
   * @param {IcalApi.Time} [opts.endDate]
   * @param {IcalApi.Time} [opts.startDate]
   * @returns {boolean}
   */
  isRecurringEventWithinRange(event, recur, { endDate, startDate } = {}) {
    // TODO V podstatě stejný kód jako u isEventWithinRange.
    // - To je částečně záměr, aby byly výsledky stejné.
    // - Ideálně by se asi měl vytvořit univerzální algoritmus, který by
    //   podporoval nekonečné události apod.
    //   Následně by se tento algoritmus omezil dle potřeb každé
    //   z variant is*WithinRange.
    // Určí se rozsah časů, ve kterém se bude hledat.
    const endTime = this.getEndTime({ endDate });
    const startTime = this.getStartTime({ startDate });
    // Začátek intervalu musí být před koncem, jinak požadavek nedává smysl.
    if (startTime > endTime) {
      return false;
    }
    // Za začátek opakované události se považuje DTSTART, za konec pak UNTIL.
    // K UNITL je ještě nutné přičíst dobu trvání události, jelikož UNTIL je
    // na rozdíl od DTEND inkluzivní.
    // Může zde tedy být výskyt události, který má DTSTART v okamžik UNTIL
    // a pak ještě celá tato událost patří do opakování, proto se přičítá
    // její doba trvání.
    // Pro úplnou přesnost by se ještě měla odečíst 1 sekunda,
    // kvůli exkluzivnímu DTEND.
    // TO se ale nedělá, protože by to zkomplikovalo výpočet pro situaci kdy
    // `startTime === endTime`.
    // Celý algoritmus ale stále není dokonalý, protože zde může být upravený
    // výskyt opakování, který tento interval přesahuje.
    // Dokonalý algoritmus by musel projít všechny upravené výskyty,
    // jestli do intervalu také nepatří.
    const eventEndDate = recur.until;
    const eventStartDate = event.startDate;
    const duration = event.duration.toSeconds();
    const eventEndTime = this.getEndTime({ endDate: eventEndDate }) + duration;
    const eventStartTime = eventStartDate.toUnixTime();
    // Kontrola validity události dle RFC 5545 kapitola 3.8.2.2.:
    // The value type of this property MUST be the same as the "DTSTART"
    // property, and its value MUST be later in time than the value
    // of the "DTSTART" property.
    // Opakované události bez kladné doby přehrávání (UNTIL dříve než DTSTART)
    // se přeskakují.
    if (eventStartTime >= eventEndTime) {
      return false;
    }
    // Pokud se nehledá v rozsahu, ale pouze jednom okamžiku, tak tento okamžik
    // musí být v intervalu <eventStartDate, eventEndDate).
    if (startTime === endTime) {
      return startTime >= eventStartTime && startTime < eventEndTime;
    }
    // Pokud se hledá v rozsahu, tak musí mít událost alespoň jeden společný
    // okamžik s rozsahem.
    // Stejně jako událost (viz. výše) je rozsah v intervalu zleva uzavřeném
    // a zprava otevřeném.
    // Proto zde není nikde rovnost, protože ať událost navazuje na interval
    // ať zleva, nebo zprava, tak nemají společný okamžik, protože se překrývá
    // otevření a uzavřený okamžik.
    return endTime > eventStartTime && startTime < eventEndTime;
  }

  /**
   * Převede JS den v týdnu na iCalendar den v týdnu,
   * pro použití u opakovaných událostí.
   * @param {number} jsWeekday JS formát: 0 = Sunday, 1 = Monday, ...,
   * 6 = Saturday
   * @returns {IcalWeekday}
   */
  parseRecurDay(jsWeekday) {
    switch (jsWeekday) {
      case 0:
        return IcalWeekday.Sunday;
      case 1:
        return IcalWeekday.Monday;
      case 2:
        return IcalWeekday.Tuesday;
      case 3:
        return IcalWeekday.Wednesday;
      case 4:
        return IcalWeekday.Thursday;
      case 5:
        return IcalWeekday.Friday;
      case 6:
        return IcalWeekday.Saturday;
      default:
        throw new RangeError('Unknown day');
    }
  }

  /**
   * Vytvoří vazby mezi událostmi, konkrétně vazby mezi opakovanými událostmi
   * a jejich úpravami výskytů opakování.
   * Díky těmto vazbám, lze pak např. používat {@link IcalApi.RecurExpansion}
   * k vyčíslení výskytů s ohledem na úpravy.
   *
   * Výjimky bez rodičovské události jsou zahozeny.
   * Duplicitní rodičovské události jsou zachovány a případné výjimky jsou
   * aplikovány na obě.
   *
   * Metoda byla přidána, protože vzhledem k bugům API není úplně triviální
   * tohoto dosáhnout při udržení rozumného výkonu.
   * @param {Array.<IcalApi.Event>} events
   * @returns {Array.<IcalApi.Event>} Seznam rodičovských událostí.
   * Je zaručeno, že žádná z těchto událostí nesplňuje
   * {@link IcalApi.Event.isRecurrenceException}.
   * K událostem výjimek lze přistupovat z rodičovské události přes
   * {@link IcalApi.Event.exceptions}.
   */
  relateEvents(events) {
    // Události se v jednom průchodu jejich seznamem rozdělí na výjimky a rodiče
    // (tj. události, které se na žádnou jinou nevážou).
    const groupedEvents = groupBy(events, (event) =>
      event.isRecurrenceException()
    );
    const exceptions = groupedEvents.true || [];
    const parents = groupedEvents.false || [];
    // Projdou se všechny výjimky a rozdělí se do skupin dle UID,
    // na které se vážou.
    const groupedExceptions = groupBy(exceptions, (exception) => exception.uid);
    // Vrátí se seznam rodičovských událostí s relacemi na jejich výjimky.
    // Pro jistotu se aktivuje i kontrola UID na straně iCal API.
    return parents.map(
      (parent) =>
        new this.ICAL.Event(parent.component, {
          exceptions: groupedExceptions[parent.uid] || [],
          strictExceptions: true
        })
    );
  }
}

export default IcalUtil;
